/**
 * EVENTS is a constant object that defines all possible tracking events in the application.
 * It is organized by category (SESSION, NAVIGATION, etc.) for better organization and type safety.
 * The 'as const' assertion ensures TypeScript treats these as literal types rather than string types.
 */
export const EVENTS = {
    SESSION: {
        START: 'session_start', // Fired when a user session begins
        END: 'session_end', // Fired when a user session ends
    },
    NAVIGATION: {
        PAGE_VIEW: 'page_view', // Tracks when a user views a new page
        TAB_CHANGE: 'tab_change', // Tracks when a user switches tabs
    },
    USER: {
        LOGIN: 'user_login', // Fired when a user logs in
        LOGOUT: 'user_logout', // Fired when a user logs out
        UPDATE_SETTINGS: 'update_settings', // Tracks user settings changes
        SESSION_CHECK: 'session_check', // Tracks session validation
        SESSION_ERROR: 'session_error', // Tracks session errors
        IDENTIFIED: 'user_identified', // Tracks user identification
    },
    AUTH: {
        API_REQUEST: 'auth_api_request', // Tracks auth API requests
        API_RESPONSE: 'auth_api_response', // Tracks auth API responses
        API_SUCCESS: 'auth_api_success', // Tracks successful auth API calls
        API_ERROR: 'auth_api_error', // Tracks auth API errors
        SIGN_IN: 'auth_sign_in', // Tracks sign in attempts
        SIGN_IN_SUCCESS: 'auth_sign_in_success', // Tracks successful sign in
        SIGN_UP: 'auth_sign_up', // Tracks sign up attempts
        SIGN_UP_SUCCESS: 'auth_sign_up_success', // Tracks successful sign up
        SIGN_OUT: 'auth_sign_out', // Tracks sign out attempts
        SIGN_OUT_SUCCESS: 'auth_sign_out_success', // Tracks successful sign out
        PASSWORD_RESET: 'auth_password_reset', // Tracks password reset attempts
        EMAIL_VERIFICATION: 'auth_email_verification', // Tracks email verification
        TOKEN_REFRESH: 'auth_token_refresh', // Tracks token refresh attempts
        SESSION_REFRESH: 'auth_session_refresh', // Tracks session refresh
        SESSION_REFRESH_SUCCESS: 'auth_session_refresh_success', // Tracks successful session refresh
        SESSION_EXPIRED: 'auth_session_expired', // Tracks when a session expires
        LICENSE_EXPIRED: 'auth_license_expired', // Tracks when a license expires
        AUTH_RESULT_STRUCTURE: 'auth_result_structure', // Tracks auth result structure
        FORGOT_PASSWORD: 'auth_forgot_password', // Tracks forgot password attempts
        FORGOT_PASSWORD_SUCCESS: 'auth_forgot_password_success', // Tracks successful forgot password
        RESET_PASSWORD: 'auth_reset_password', // Tracks password reset attempts
        RESET_PASSWORD_SUCCESS: 'auth_reset_password_success', // Tracks successful password reset
        RESEND_VERIFICATION: 'auth_resend_verification', // Tracks resend verification attempts
        RESEND_VERIFICATION_SUCCESS: 'auth_resend_verification_success', // Tracks successful verification resend
        VERIFY_EMAIL: 'auth_verify_email', // Tracks email verification attempts
        VERIFY_EMAIL_SUCCESS: 'auth_verify_email_success', // Tracks successful email verification
        SET_AUTH_COOKIES: 'auth_set_auth_cookies', // Tracks setting auth cookies
        SKIP_COGNITO_COOKIE_SETTING: 'auth_skip_cognito_cookie_setting', // Tracks skipping Cognito cookie setting
        CLEAR_AUTH_COOKIES: 'auth_clear_auth_cookies', // Tracks clearing auth cookies
        SKIP_COGNITO_COOKIE_CLEARING: 'auth_skip_cognito_cookie_clearing', // Tracks skipping Cognito cookie clearing
        GET_AUTH_COOKIES: 'auth_get_auth_cookies', // Tracks getting auth cookies
        SKIP_COGNITO_COOKIE_RETRIEVAL: 'auth_skip_cognito_cookie_retrieval', // Tracks skipping Cognito cookie retrieval
        GET_PROFILE: 'auth_get_profile', // Tracks getting user profile
        GET_ORG_PROFILE: 'auth_get_org_profile', // Tracks getting organization profile
        GET_SERVER_SESSION: 'auth_get_server_session', // Tracks getting server session
        GET_SERVER_SESSION_SUCCESS: 'auth_get_server_session_success', // Tracks successful server session retrieval
        NO_ID_TOKEN_FOUND: 'auth_no_id_token_found', // Tracks when no ID token is found
        USING_MOCK_USER_PROFILE_FOR_DEVELOPMENT_MODE:
            'auth_using_mock_user_profile_for_development_mode', // Tracks using mock user profile for development mode
        REFRESH_TOKEN: 'auth_refresh_token', // Tracks refreshing token
        SESSION_TOKEN: 'auth_session_token', // Tracks session token retrieval
        USING_MOCK_AUTH_RESULT_FOR_DEVELOPMENT_MODE_REFRESH:
            'auth_using_mock_auth_result_for_development_mode_refresh', // Tracks using mock auth result for development mode refresh
        USING_DEVELOPMENT_MODE_SIGN_IN: 'auth_using_development_mode_sign_in', // Tracks using development mode sign in
        USING_DEVELOPMENT_MODE_SIGN_IN_SUCCESS: 'auth_using_development_mode_sign_in_success', // Tracks successful development mode sign in
        NO_REFRESH_TOKEN_FOUND: 'auth_no_refresh_token_found', // Tracks when no refresh token is found
    },
    SURVEY: {
        CREATE: 'survey_create', // Tracks survey creation
        UPDATE: 'survey_update', // Tracks survey updates
        DELETE: 'survey_delete', // Tracks survey deletion
        EXPORT: 'survey_export', // Tracks survey exports
    },
    ANALYSIS: {
        FILTER_APPLY: 'filter_apply', // Tracks when filters are applied
        EXPORT_START: 'export_start', // Tracks start of data export
        EXPORT_COMPLETE: 'export_complete', // Tracks successful export completion
        EXPORT_ERROR: 'export_error', // Tracks export failures
    },
    PERFORMANCE: {
        PAGE_LOAD: 'page_load', // Tracks page load timing
        LCP: 'largest_contentful_paint', // Tracks Largest Contentful Paint metric
        API_LATENCY: 'api_latency', // Tracks API response times
        // One per DANI answer: value is ms from send to the end; context has
        // the first event and first content times (utils/answer-timing.ts).
        DANI_ANSWER_TIME: 'dani_answer_time',
        MEMORY: 'memory_usage', // Tracks memory consumption
        LONG_TASK: 'long_task', // Tracks long-running tasks
    },
    PORTAL: {
        PROJECT_INIT: 'portal_project_init', // Tracks project initialization
        SURVEY_SELECTION: 'portal_survey_selection', // Tracks survey selection
        SECTION_CHANGE: 'portal_section_change', // Tracks section navigation
        FILTER_UPDATED: 'portal_filter_updated', // Tracks filter updates
        FILTER_DELETED: 'portal_filter_deleted', // Tracks filter deletions
        FILTER_CREATED: 'portal_filter_created', // Tracks filter creations
    },
    API: {
        REQUEST: 'api_request', // Tracks API request start
        SUCCESS: 'api_success', // Tracks successful API response
        ERROR: 'api_error', // Tracks API errors
        RETRY: 'api_retry', // Tracks API retry attempts
    },
    QUESTIONS: {
        FETCH_START: 'questions_fetch_start', // Start of questions fetch
        FETCH_SUCCESS: 'questions_fetch_success', // Successful questions fetch
        FETCH_ERROR: 'questions_fetch_error', // Failed questions fetch
        FETCH_EMPTY: 'questions_fetch_empty', // No questions found
        FETCH_ENABLED: 'questions_fetch_enabled', // Questions fetch enabled
        PREFETCH_START: 'questions_prefetch_start', // Start of questions prefetch
        AGGREGATION_START: 'questions_aggregation_start', // Start of aggregation fetch
        AGGREGATION_SUCCESS: 'questions_aggregation_success', // Successful aggregation fetch
        AGGREGATION_ERROR: 'questions_aggregation_error', // Failed aggregation fetch
        VISIBILITY_UPDATE_START: 'questions_visibility_update_start', // Start of visibility update
        VISIBILITY_UPDATE_SUCCESS: 'questions_visibility_update_success', // Successful visibility update
        VISIBILITY_UPDATE_ERROR: 'questions_visibility_update_error', // Failed visibility update
        COMPACT_LIST: {
            EMPTY_STATE_CHANGE: 'questions_empty_state_change', // Question list empty state change
            SIZE_CHANGE: 'questions_size_change', // Question list size change
            SCROLL_START: 'questions_scroll_start', // Question list scroll start
            SCROLL_END: 'questions_scroll_end', // Question list scroll end
            RENDER_TIME: 'questions_render_time', // Question list render time
            SCROLL_DURATION: 'questions_scroll_duration', // Question list scroll duration
        },
        SEARCH: 'questions_search', // Question search
        FILTER: 'questions_filter', // Question filtering
        SORT: 'questions_sort', // Question sorting
    },
    STUDIO: {
        API_REQUEST: 'studio_api_request', // Studio API request
        API_RESPONSE: 'studio_api_response', // Studio API response
        API_ERROR: 'studio_api_error', // Studio API error
        API_SUCCESS: 'studio_api_success', // Studio API success
        API_RETRY: 'studio_api_retry', // Studio API retry
        API_RETRY_SUCCESS: 'studio_api_retry_success', // Studio API retry success
        API_RETRY_ERROR: 'studio_api_retry_error', // Studio API retry error
        SEND_SUPPORT_TICKET_ATTEMPT: 'studio_send_support_ticket_attempt', // Attempt to send support ticket
        SEND_SUPPORT_TICKET_SUCCESS: 'studio_send_support_ticket_success', // Successful support ticket send
        USER_ROUTE_VISIT: 'studio_user_route_visit', // User route visit logging
        INSIGHT_CTA_RESOLVE: 'studio_insight_cta_resolve', // CTA token resolved on trial page
        INSIGHT_CTA_FIRST_MESSAGE: 'studio_insight_cta_first_message', // First freemium message on trial page
        INSIGHT_CTA_LIMIT_HIT: 'studio_insight_cta_limit_hit', // Freemium limit hit on trial page
        INSIGHT_CTA_LOGIN_REDIRECT: 'studio_insight_cta_login_redirect', // requiresLogin shown on trial page
    },
    FEASIBILITY: {
        CREATE: 'feasibility_create', // Tracks feasibility creation
        UPDATE: 'feasibility_update', // Tracks feasibility update
        DELETE: 'feasibility_delete', // Tracks feasibility deletion
        EXPORT: 'feasibility_export', // Tracks feasibility export
        FILTER: 'feasibility_filter', // Tracks feasibility filter
    },
    SALESFORCE: {
        CREATE: 'salesforce_create', // Tracks salesforce creation
        UPDATE: 'salesforce_update', // Tracks salesforce update
        DELETE: 'salesforce_delete', // Tracks salesforce deletion
        EXPORT: 'salesforce_export', // Tracks salesforce export
    },
    DISCOVER: {
        // Fired exactly once per "Dive deep with DANI" click on a Discover →
        // Insights card — the attribution event the PRD's success metric
        // depends on. Carries insightId so query volume is traceable back to
        // the originating insight; must NOT re-fire for in-session follow-ups.
        INSIGHT_CTA_CLICK: 'discover_insight_cta_click',
    },
    CREDIT_CONSUMPTION: {
        DASHBOARD_VIEWED: 'credit_consumption_dashboard_viewed',
        TOGGLE_CHANGED: 'credits_dollars_toggle_changed',
        DANI_CAP_WARNING_VIEWED: 'dani_cap_warning_banner_viewed',
        ANNUAL_POOL_EXCEEDED_VIEWED: 'annual_pool_exceeded_banner_viewed',
        FORECAST_CAPACITY_CLICKED: 'forecast_capacity_clicked',
        RECOMMEND_TIER_CLICKED: 'recommend_credit_tier_clicked',
        LEDGER_PAGINATED: 'transaction_ledger_paginated',
        USER_BREAKDOWN_EXPANDED: 'dani_user_breakdown_expanded',
        ORG_TABLE_FILTERED: 'csm_org_table_filtered',
        ORG_ROW_CLICKED: 'csm_org_row_clicked',
        FLEET_ROW_CLICKED: 'credit_fleet_row_clicked',
        RATE_CARD_UNAVAILABLE: 'rate_card_unavailable',
        RATE_VERSION_PUBLISHED: 'rate_version_published',
    },
    DANI_SYNTHESIS: {
        ANALYSIS_STARTED: 'dani_synthesis_analysis_started',
        RERUN_TRIGGERED: 'dani_synthesis_rerun_triggered',
        COMPARE_RUN: 'dani_synthesis_compare_run', // Comparison computed and persisted (cache miss)
        COMPARE_CACHE_HIT: 'dani_synthesis_compare_cache_hit', // Unchanged comparison re-served from the stored row
        COMPARE_OPENED: 'dani_synthesis_compare_opened', // Comparison view opened / re-opened
        COMPARE_EVIDENCE_OPENED: 'dani_synthesis_compare_evidence_opened', // Per-cell evidence drawer opened
        COMPARE_NARRATIVE_GENERATED: 'dani_synthesis_compare_narrative_generated', // Narrative layer generated for a comparison
    },
    INSIGHT_ENGINE: {
        EMAIL_LINK_GENERATED: 'insight_engine_email_link_generated',
        EMAIL_LINK_CONSUMED: 'insight_engine_email_link_consumed',
    },
    // Trial onboarding (M4S-5566). Its own consumed event rather than a shared
    // one with INSIGHT_ENGINE above: both programmes deep-link to /dani?prompt=
    // and are consumed by the same hook, so a single event would count every
    // onboarding click as an insight-engine conversion — and this epic's
    // success metric is read off the onboarding one.
    ONBOARDING_EMAIL: {
        LINK_CONSUMED: 'onboarding_email_link_consumed',
    },
    // Full Crosstabs (M4S-5777, docs/pdlc/crosstabs-scale-and-retirement.md). Timing metrics for
    // the open and each result (X138), a cancelled load (X138), and every use of the classic tool,
    // watched through the quiet period before it is switched off (X148).
    CROSSTABS: {
        OPEN_TIMING: 'crosstab_open_timing',
        RUN_TIMING: 'crosstab_run_timing',
        LOAD_CANCELLED: 'crosstab_load_cancelled',
        CLASSIC_USED: 'crosstab_classic_used',
        // A file left the workspace: one per successful export, with its format. The adoption
        // dashboard counts these as crosstabs delivered (docs/pdlc/feature-adoption.md).
        EXPORTED: 'crosstab_exported',
    },
    // DANI memory, phase 0 (docs/prd/M4S-6009-dani-memory.md, A1/A2): the baseline of how many
    // messages a client sends before DANI's first data answer, and feedback on answers.
    DANI_ANSWERS: {
        FIRST_DATA_ANSWER: 'dani_first_data_answer',
        ANSWER_FEEDBACK: 'dani_answer_feedback',
    },
} as const;

/**
 * Type definitions for each event category.
 * These types are derived from the EVENTS object and provide type safety when working with events.
 */
export type SessionEventType = (typeof EVENTS.SESSION)[keyof typeof EVENTS.SESSION];
export type NavigationEventType = (typeof EVENTS.NAVIGATION)[keyof typeof EVENTS.NAVIGATION];
export type UserEventType = (typeof EVENTS.USER)[keyof typeof EVENTS.USER];
export type AuthEventType = (typeof EVENTS.AUTH)[keyof typeof EVENTS.AUTH];
export type SurveyEventType = (typeof EVENTS.SURVEY)[keyof typeof EVENTS.SURVEY];
export type AnalysisEventType = (typeof EVENTS.ANALYSIS)[keyof typeof EVENTS.ANALYSIS];
export type PerformanceEventType = (typeof EVENTS.PERFORMANCE)[keyof typeof EVENTS.PERFORMANCE];
export type PortalEventType = (typeof EVENTS.PORTAL)[keyof typeof EVENTS.PORTAL];
export type ApiEventType = (typeof EVENTS.API)[keyof typeof EVENTS.API];
export type QuestionsEventType =
    | (typeof EVENTS.QUESTIONS)[keyof typeof EVENTS.QUESTIONS]
    | (typeof EVENTS.QUESTIONS.COMPACT_LIST)[keyof typeof EVENTS.QUESTIONS.COMPACT_LIST];
export type StudioEventType = (typeof EVENTS.STUDIO)[keyof typeof EVENTS.STUDIO];
export type FeasibilityEventType = (typeof EVENTS.FEASIBILITY)[keyof typeof EVENTS.FEASIBILITY];
export type SalesforceEventType = (typeof EVENTS.SALESFORCE)[keyof typeof EVENTS.SALESFORCE];
export type DiscoverEventType = (typeof EVENTS.DISCOVER)[keyof typeof EVENTS.DISCOVER];
export type CreditConsumptionEventType =
    (typeof EVENTS.CREDIT_CONSUMPTION)[keyof typeof EVENTS.CREDIT_CONSUMPTION];
export type DaniSynthesisEventType =
    (typeof EVENTS.DANI_SYNTHESIS)[keyof typeof EVENTS.DANI_SYNTHESIS];
export type InsightEngineEventType =
    (typeof EVENTS.INSIGHT_ENGINE)[keyof typeof EVENTS.INSIGHT_ENGINE];
export type OnboardingEmailEventType =
    (typeof EVENTS.ONBOARDING_EMAIL)[keyof typeof EVENTS.ONBOARDING_EMAIL];
export type CrosstabsEventType = (typeof EVENTS.CROSSTABS)[keyof typeof EVENTS.CROSSTABS];
export type DaniAnswersEventType = (typeof EVENTS.DANI_ANSWERS)[keyof typeof EVENTS.DANI_ANSWERS];

/**
 * Union type of all possible event names
 */
export type EventName =
    | SessionEventType
    | NavigationEventType
    | UserEventType
    | AuthEventType
    | SurveyEventType
    | AnalysisEventType
    | PerformanceEventType
    | PortalEventType
    | ApiEventType
    | QuestionsEventType
    | StudioEventType
    | FeasibilityEventType
    | SalesforceEventType
    | DiscoverEventType
    | CreditConsumptionEventType
    | DaniSynthesisEventType
    | InsightEngineEventType
    | OnboardingEmailEventType
    | CrosstabsEventType
    | DaniAnswersEventType;

/**
 * Type guard functions
 * These functions check if a given event belongs to a specific category.
 * They provide type safety and runtime checking capabilities.
 */
export const isSessionEvent = (event: EventName): event is SessionEventType =>
    Object.values(EVENTS.SESSION).includes(event as SessionEventType);

export const isNavigationEvent = (event: EventName): event is NavigationEventType =>
    Object.values(EVENTS.NAVIGATION).includes(event as NavigationEventType);

export const isUserEvent = (event: EventName): event is UserEventType =>
    Object.values(EVENTS.USER).includes(event as UserEventType);

export const isAuthEvent = (event: EventName): event is AuthEventType =>
    Object.values(EVENTS.AUTH).includes(event as AuthEventType);

export const isSurveyEvent = (event: EventName): event is SurveyEventType =>
    Object.values(EVENTS.SURVEY).includes(event as SurveyEventType);

export const isAnalysisEvent = (event: EventName): event is AnalysisEventType =>
    Object.values(EVENTS.ANALYSIS).includes(event as AnalysisEventType);

export const isPerformanceEvent = (event: EventName): event is PerformanceEventType =>
    Object.values(EVENTS.PERFORMANCE).includes(event as PerformanceEventType);

export const isPortalEvent = (event: EventName): event is PortalEventType =>
    Object.values(EVENTS.PORTAL).includes(event as PortalEventType);

export const isApiEvent = (event: EventName): event is ApiEventType =>
    Object.values(EVENTS.API).includes(event as ApiEventType);

export const isQuestionsEvent = (event: EventName): event is QuestionsEventType =>
    Object.values(EVENTS.QUESTIONS).includes(event as any) ||
    Object.values(EVENTS.QUESTIONS.COMPACT_LIST).includes(event as any);

export const isStudioEvent = (event: EventName): event is StudioEventType =>
    Object.values(EVENTS.STUDIO).includes(event as StudioEventType);

export const isFeasibilityEvent = (event: EventName): event is FeasibilityEventType =>
    Object.values(EVENTS.FEASIBILITY).includes(event as FeasibilityEventType);

export const isSalesforceEvent = (event: EventName): event is SalesforceEventType =>
    Object.values(EVENTS.SALESFORCE).includes(event as SalesforceEventType);

export const isDiscoverEvent = (event: EventName): event is DiscoverEventType =>
    Object.values(EVENTS.DISCOVER).includes(event as DiscoverEventType);

export const isCrosstabsEvent = (event: EventName): event is CrosstabsEventType =>
    Object.values(EVENTS.CROSSTABS).includes(event as CrosstabsEventType);

/**
 * Helper function that determines the category of a given event.
 * This is useful for analytics grouping and filtering.
 *
 * @param event - The event name to categorize
 * @returns The category name as a string, or 'UNKNOWN' if the event doesn't match any category
 */
export const getEventCategory = (event: EventName): string => {
    if (isSessionEvent(event)) return 'SESSION';
    if (isNavigationEvent(event)) return 'NAVIGATION';
    if (isUserEvent(event)) return 'USER';
    if (isAuthEvent(event)) return 'AUTH';
    if (isSurveyEvent(event)) return 'SURVEY';
    if (isAnalysisEvent(event)) return 'ANALYSIS';
    if (isPerformanceEvent(event)) return 'PERFORMANCE';
    if (isPortalEvent(event)) return 'PORTAL';
    if (isApiEvent(event)) return 'API';
    if (isQuestionsEvent(event)) return 'QUESTIONS';
    if (isStudioEvent(event)) return 'STUDIO';
    if (isFeasibilityEvent(event)) return 'FEASIBILITY';
    if (isSalesforceEvent(event)) return 'SALESFORCE';
    if (isDiscoverEvent(event)) return 'DISCOVER';
    if (isCrosstabsEvent(event)) return 'CROSSTABS';
    return 'UNKNOWN';
};
